import { db, collection, getDocs } from '../components/firebase';
import { SUBJECTS } from '../config/subjects';

/**
 * Normalizes a string to PascalCase for collection names
 */
const toPascalCase = (str) => {
    return str
        .replace(/[^a-zA-Z0-9]/g, ' ')
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join('');
};

/**
 * Resolves the collection names for a given subject entry
 */
export const getSubjectCollections = (subject) => {
    // Common mappings for subjects that don't follow the default pattern exactly
    const overrides = {
        'Applied Chemistry': 'AppliedChemistry',
        'Applied Math II': 'AppliedMathsII',
        'Applied Physics I': 'AppliedPhysicsI',
        'Applied Math I': 'AppliedMathsI',
        'Applied Physics II': 'AppliedPhysicsII',
        'Digital Logic and Circuit Design': 'DLCD',
        'Computer Networks': 'CN',
        'Operating Systems': 'OS',
        'Software Engineering': 'SE',
        'Design Analysis and Algorithms': 'DAA',
        'Database Management System': 'DBMS',
        'Theory of Computation': 'TOC',
        'Technical Writing': 'TW',
        'Object Oriented Programming': 'OOPS',
        'Discrete Mathematics': 'DM',
        'Data Structures': 'DS',
        'Artificial Intelligence': 'AI',
        'Programming in Python': 'PPY',
        'Programming In C': 'PC',
        'Communication Skills': 'CommunicationSkills',
        'Engineering Mechanics': 'Engineering_Mechanics',
        'Electrical Science': 'Electrical_Science',
        'Environmental Studies': 'Enviromental_Studies',
        'Human Values and Ethics': 'Human values and Ethics',
        'Indian Constitution': 'Indian_Constitution',
        'Manufacturing Process': 'Manufacturing_Process'
    };

    const base = overrides[subject.label] || toPascalCase(subject.label);

    // Most subjects use PascalCaseNotes/Videos if they are "Notes" routes
    if (subject.path.includes('/notes/')) {
        return {
            notes: `${base}Notes`,
            videos: `${base}Videos`
        };
    }

    return null;
};

/**
 * Fetches the total count of all study materials (PDFs and Videos)
 */
export const getTotalStudyMaterialCount = async () => {
    try {
        // 1. Get all subject note/video collections
        const collectionsToFetch = [];
        SUBJECTS.forEach(subject => {
            const colls = getSubjectCollections(subject);
            if (colls) {
                collectionsToFetch.push(colls.notes);
                collectionsToFetch.push(colls.videos);
            }
        });

        // 2. Filter unique collections (some subjects might be duplicated in registry)
        const uniqueCollections = [...new Set(collectionsToFetch)];

        // 3. Fetch sizes in parallel (Note: getDocs for size is heavy, but accurate for now)
        // We limit batching to avoid overwhelming Firestore if needed, but for ~100 unique ones it's okay.
        const counts = await Promise.all(uniqueCollections.map(async (collName) => {
            try {
                const snap = await getDocs(collection(db, collName));
                return snap.size;
            } catch (err) {
                // Collection might not exist yet
                return 0;
            }
        }));

        // 4. Add tutorials (posts)
        const postsSnap = await getDocs(collection(db, 'posts'));
        const tutorialsCount = postsSnap.size;

        const total = counts.reduce((acc, count) => acc + count, 0) + tutorialsCount;
        return total;
    } catch (error) {
        console.error("Error calculating deep study material count:", error);
        return 500; // Fallback to estimated count
    }
};
